import { Zap, Battery, TrendingUp, Plug, Cable, Gauge } from 'lucide-react';
import { InfoSection, InfoField } from '@/core/components/ui/detail-blade';
import { KioskSessionGridRow, KioskSessionDetail } from '../types/kiosk-session-types';

interface EnergyInfoSectionProps {
  session: KioskSessionGridRow | null;
  detail: KioskSessionDetail | undefined;
}

export function EnergyInfoSection({ session, detail }: EnergyInfoSectionProps) {
  if (!detail) {
    return null;
  }

  const formatEnergy = (energy: number) => {
    if (!energy) return 'N/A';
    return `${energy.toFixed(2)} kWh`;
  };

  const formatPower = (energy: number, duration: string) => {
    if (!energy || !duration) return 'N/A';

    // Parse duration (assuming format like "HH:MM:SS" or similar)
    const durationParts = duration.split(':');
    if (durationParts.length >= 2) {
      const hours = parseInt(durationParts[0]) + parseInt(durationParts[1]) / 60;
      const avgPower = hours > 0 ? energy / hours : 0;
      return `${avgPower.toFixed(2)} kW`;
    }

    return 'N/A';
  };

  const formatMaxPower = (maxPower: number | undefined) => {
    if (maxPower == null) return 'N/A';
    return `${maxPower.toFixed(2)} kW`;
  };

  const formatConnectorStandard = (standard: string | undefined) => {
    if (!standard || standard === 'Unknown') return 'N/A';
    // Make connector type more readable
    return standard
      .replace('IEC_62196_', '')
      .replace('_COMBO', ' Combo')
      .replace(/_/g, ' ');
  };

  const formatPowerType = (powerType: string | undefined) => {
    if (!powerType || powerType === 'UNKNOWN') return 'N/A';
    return powerType
      .replace('AC_1_PHASE', 'AC Single Phase')
      .replace('AC_2_PHASE', 'AC Two Phase')
      .replace('AC_2_PHASE_SPLIT', 'AC Split Phase')
      .replace('AC_3_PHASE', 'AC Three Phase')
      .replace('DC', 'DC Fast Charging');
  };

  const formatConnectorFormat = (format: string | undefined) => {
    if (!format || format === 'UNKNOWN') return 'N/A';
    return format === 'CABLE' ? 'Tethered Cable' : 'Socket';
  };

  return (
    <InfoSection
      title="Energy & Charging"
      icon={Zap}
      collapsible={true}
      defaultExpanded={true}
      storageKey="kioskSessionDetail.energyInfo.expanded"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <InfoField
            label="Total Energy"
            value={formatEnergy(detail.total_energy)}
            icon={Battery}
          />

          <InfoField
            label="Average Power"
            value={formatPower(detail.total_energy, detail.duration)}
            icon={TrendingUp}
          />

          {detail.max_power != null && (
            <InfoField
              label="Max Power"
              value={formatMaxPower(detail.max_power)}
              icon={Gauge}
            />
          )}
        </div>

        <div className="space-y-3">
          <InfoField
            label="CDR"
            value={session?.cdr_id || detail.cdr_id}
            type="id"
            copyable
          />

          {detail.connector_standard && detail.connector_standard !== 'Unknown' && (
            <InfoField
              label="Connector Type"
              value={formatConnectorStandard(detail.connector_standard)}
              icon={Plug}
            />
          )}

          {detail.power_type && detail.power_type !== 'UNKNOWN' && (
            <InfoField
              label="Power Type"
              value={formatPowerType(detail.power_type)}
              icon={Zap}
            />
          )}

          {detail.connector_format && detail.connector_format !== 'UNKNOWN' && (
            <InfoField
              label="Connector Format"
              value={formatConnectorFormat(detail.connector_format)}
              icon={Cable}
            />
          )}

          {detail.connector_alias && (
            <InfoField
              label="Connector Name"
              value={detail.connector_alias}
            />
          )}
        </div>
      </div>
    </InfoSection>
  );
}